import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { IntakeFormValues, UUID } from '@aline/common';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import { ChevronLeft, CirclePlus, GripVertical, SquarePen, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineButton } from 'src/components';
import { AlineAlert } from 'src/components/generic/AlineAlert';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { BuilderFieldType, DraftIntakeField } from './intake-form-fields';
import {
  BUILDER_FIELD_META,
  BUILDER_FIELD_TYPES,
  buildFormDefinitionFromDraft,
  createBlankField,
  draftFieldsFromDefinition,
  getDraftFieldIsComplete,
  INTAKE_FORM_TITLE_INPUT_PLACEHOLDER,
  INTAKE_FORM_TITLE_TEXT_PLACEHOLDER,
} from './intake-form-fields';
import { IntakeFormFieldEditor } from './IntakeFormFieldEditor';
import { IntakeFormFields } from './IntakeFormFields';
import {
  createIntakeFormBuilderSaveSnapshot,
  useIntakeFormBuilderPersistence,
} from './use-intake-form-builder-persistence';
import type { IntakeFormBuilderSaveSnapshot } from './use-intake-form-builder-persistence';

type BuilderField = DraftIntakeField & { uid: string };
type BuildSaveSnapshotResult = { snapshot: IntakeFormBuilderSaveSnapshot } | { error: string };

interface EditorState {
  draft: DraftIntakeField;
  uid: string | null;
}

async function handleDisabledPreviewFileRemoval() {
  return undefined;
}

const BUILDER_PANE_MIN_HEIGHT = 420;

const useStyles = makeStyles()(theme => ({
  addFieldButton: {
    alignItems: 'center',
    alignSelf: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 48,
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    fontWeight: 'bold',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.5),
  },

  back: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    paddingTop: theme.spacing(0.5),
  },

  canvas: {
    border: `1px dashed ${theme.appColors.rule ?? '#c8d0d4'}`,
    borderRadius: 12,
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxHeight: '60vh',
    minHeight: BUILDER_PANE_MIN_HEIGHT,
    overflow: 'hidden',
    padding: theme.spacing(1.5),
  },

  canvasBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 0,
    overflowY: 'auto',
  },
  configPane: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  /**
   * MUI v9 makes the dialog Paper a focus target (tabIndex -1) and only zeroes
   * the root's outline, so the focus-trap leaves a browser focus ring on the
   * Paper when the modal opens. The Paper isn't an interactive control, so drop
   * its outline (interactive children keep their own focus styles).
   */
  dialogPaper: {
    '&:focus, &:focus-visible': { outline: 'none' },
  },
  divider: {
    borderTop: `1px solid ${theme.appColors.rule ?? 'rgba(217,217,217,0.6)'}`,
  },
  eyebrow: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  fieldLabel: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
  },
  fieldMeta: {
    flex: 1,
    minWidth: 0,
  },
  fieldRow: {
    '&:hover': { background: theme.appColors.hoverBackground ?? '#f5f5f5' },
    '&:hover .intake-field-row-actions': { opacity: 1 },
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
  },
  fieldSubtitle: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
  },
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, 1fr)',
    margin: 'auto',
    maxWidth: 560,
    width: '100%',
  },
  gridCard: {
    '&:hover': { borderColor: theme.appColors.primary },
    alignItems: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 12,
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    minHeight: 120,
  },
  gridLabel: {
    fontSize: 14,
    fontWeight: 600,
  },
  handle: {
    alignItems: 'center',
    color: theme.appColors.iconsType ?? 'rgba(55,73,81,0.4)',
    cursor: 'grab',
    display: 'flex',
    touchAction: 'none',
  },
  menuItem: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  previewBody: {
    /**
     * Paint containment (not overflow) is required to keep the preview's CSS
     * grid from propagating its scrollable overflow past this box and growing
     * the whole modal — same fix as IntakeFormSidePanel.content.
     */
    contain: 'paint',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1.5),
  },
  previewBox: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 12,
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    maxHeight: '60vh',
    minHeight: BUILDER_PANE_MIN_HEIGHT,
    overflow: 'hidden',
  },
  previewEmpty: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    flex: 1,
    fontSize: 14,
    justifyContent: 'center',
    textAlign: 'center',
  },
  previewPane: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  rowAction: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: '#374951',
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  rowActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    opacity: 0,
  },
  sectionLabel: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
  },
  titleDisplay: {
    fontSize: 22,
    fontWeight: 'bold',
  },
  titleInput: {
    fontSize: 22,
    fontWeight: 'bold',
    padding: 0,
  },
  titleWrap: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
  },
  twoPane: {
    alignItems: 'stretch',
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: '1fr 1fr',
  },
}));

interface IntakeFormBuilderModalProps {
  existingTemplate: ApolloTypes.IntakeFormTemplateFullFragment | null;
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}

export function IntakeFormBuilderModal({
  existingTemplate,
  onClose,
  open,
  workflowId,
}: IntakeFormBuilderModalProps) {
  const nextUid = useRef(0);
  const { classes } = useStyles();
  const prevOpenRef = useRef(false);
  const [title, setTitle] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const makeUid = () => `f${nextUid.current++}`;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [pendingDeleteField, setPendingDeleteField] = useState<BuilderField | null>(null);
  // Throwaway values so the live preview is interactive ("play around"); never saved.
  const [previewValues, setPreviewValues] = useState<IntakeFormValues>({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const { currentTemplateId, flushTitleSave, initialize, saveNow, scheduleTitleSave } =
    useIntakeFormBuilderPersistence(workflowId);

  const buildSaveSnapshot = useCallback(
    (nextTitle: string, nextFields: BuilderField[]): BuildSaveSnapshotResult => {
      const built = buildFormDefinitionFromDraft(nextFields.map(({ uid, ...field }) => field));
      if ('error' in built) return { error: built.error };

      return {
        snapshot: createIntakeFormBuilderSaveSnapshot({
          definition: built.definition,
          fieldCount: nextFields.length,
          title: nextTitle,
        }),
      };
    },
    []
  );

  const saveFieldsNow = useCallback(
    (nextFields: BuilderField[]) => {
      const result = buildSaveSnapshot(title, nextFields);
      if ('error' in result) {
        enqueueSnackbar(result.error, { variant: 'error' });
        return false;
      }
      saveNow(result.snapshot);
      return true;
    },
    [buildSaveSnapshot, enqueueSnackbar, saveNow, title]
  );

  const saveTitleLater = useCallback(
    (nextTitle: string) => {
      const result = buildSaveSnapshot(nextTitle, fields);
      if ('error' in result) {
        enqueueSnackbar(result.error, { variant: 'error' });
        return;
      }
      setTitle(nextTitle);
      scheduleTitleSave(result.snapshot);
    },
    [buildSaveSnapshot, enqueueSnackbar, fields, scheduleTitleSave]
  );

  /**
   * Hydrate from the existing template ONLY on the open transition — not on every
   * `existingTemplate` cache update (a save triggers one), so re-hydrating
   * never clobbers in-progress edits or closes the open field editor. Flush any
   * pending title save on the close transition (programmatic closes).
   */
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    const justClosed = !open && prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened) {
      setEditor(null);
      setPreviewValues({});
      const initialTitle = existingTemplate?.title ?? '';
      setTitle(initialTitle);
      const drafts = existingTemplate
        ? draftFieldsFromDefinition(existingTemplate.formDefinition)
        : [];
      const initialFields = drafts.map(draft => ({ ...draft, uid: makeUid() }));
      const initialSnapshot = buildSaveSnapshot(initialTitle, initialFields);
      setFields(initialFields);
      initialize({
        isAttached: Boolean(existingTemplate),
        lastSavedSnapshot: 'snapshot' in initialSnapshot ? initialSnapshot.snapshot : null,
        templateId: existingTemplate?.id ?? null,
      });
    }
    if (justClosed) flushTitleSave();
  }, [buildSaveSnapshot, existingTemplate, flushTitleSave, initialize, open]);

  const { data: attachmentsData } = Apollo.useIntakeFormTemplateAttachedWorkflowsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !currentTemplateId,
    variables: { intakeFormTemplateId: currentTemplateId ?? '' },
  });
  const attachedWorkflows = attachmentsData?.intakeFormTemplate?.attachedWorkflows ?? [];

  const startAdd = (type: BuilderFieldType) => {
    setAddOpen(false);
    setEditor({ draft: createBlankField(type), uid: null });
  };

  const commitEditor = () => {
    if (!editor) return;
    const nextFields =
      editor.uid === null
        ? [...fields, { ...editor.draft, uid: makeUid() }]
        : fields.map(field =>
            field.uid === editor.uid ? { ...editor.draft, uid: editor.uid } : field
          );
    if (!saveFieldsNow(nextFields)) return;
    setFields(nextFields);
    setEditor(null);
  };

  /**
   * Save a complete editor draft before closing so the user doesn't lose it; an
   * unfinished draft is discarded as before.
   */
  const handleClose = () => {
    flushTitleSave();
    if (editor && getDraftFieldIsComplete(editor.draft)) {
      const nextFields =
        editor.uid === null
          ? [...fields, { ...editor.draft, uid: makeUid() }]
          : fields.map(field =>
              field.uid === editor.uid ? { ...editor.draft, uid: editor.uid } : field
            );
      if (!saveFieldsNow(nextFields)) return;
      setFields(nextFields);
      setEditor(null);
      onClose();
      return;
    }
    onClose();
  };

  /**
   * The editor's trash removes an existing field; for a new (unsaved) field it
   * just discards the draft.
   */
  const deleteEditingField = () => {
    const uid = editor?.uid;
    if (uid) {
      setPendingDeleteField({ ...editor.draft, uid });
      return;
    }
    setEditor(null);
  };

  const confirmDeleteField = () => {
    if (!pendingDeleteField) return;

    const nextFields = fields.filter(field => field.uid !== pendingDeleteField.uid);
    if (!saveFieldsNow(nextFields)) return;
    setFields(nextFields);
    if (editor?.uid === pendingDeleteField.uid) setEditor(null);
    setPendingDeleteField(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = fields.findIndex(f => f.uid === active.id);
    const to = fields.findIndex(f => f.uid === over.id);
    if (from === -1 || to === -1) return;

    const nextFields = arrayMove(fields, from, to);
    if (!saveFieldsNow(nextFields)) return;
    setFields(nextFields);
  };

  const showEmptyGrid = fields.length === 0 && !editor;
  // The field being edited shows in the editor card, not (also) in the list.
  const visibleFields = fields.filter(field => field.uid !== editor?.uid);
  const previewDefinition = useMemo(() => {
    const built = buildFormDefinitionFromDraft(fields.map(({ uid, ...field }) => field));
    return 'definition' in built ? built.definition : undefined;
  }, [fields]);
  /**
   * A never-saved field has key='' (its name is derived only at save time), so
   * keying the preview by `key` would collide every new field onto a single
   * value slot. Key the preview by the derived definition name (when the draft
   * is valid) or the unique builder uid, so preview values + validation are
   * per-field.
   */
  const previewFields = useMemo(
    () =>
      fields.map((field, index) => {
        const { uid, ...rest } = field;
        return { ...rest, key: previewDefinition?.fields[index]?.name || rest.key || uid };
      }),
    [fields, previewDefinition]
  );
  const sharedTemplateWarning = `This intake form is used by ${
    attachedWorkflows.length
  } workflow templates (${attachedWorkflows
    .map(w => w.name || 'Untitled workflow')
    .join(', ')}). Changes apply to all of them.`;

  return (
    <>
      <AlineDialog
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        maxWidth="lg"
        open={open}
        title={
          <div className={classes.titleWrap}>
            <ButtonBase aria-label="Back" className={classes.back} onClick={handleClose}>
              <ChevronLeft size={22} />
            </ButtonBase>
            <div>
              <div className={classes.eyebrow}>Intake Form</div>
              <InlineEditField
                classes={{ display: classes.titleDisplay, input: classes.titleInput }}
                iconSize={16}
                placeholder={INTAKE_FORM_TITLE_INPUT_PLACEHOLDER}
                textPlaceholder={INTAKE_FORM_TITLE_TEXT_PLACEHOLDER}
                value={title}
                onSave={saveTitleLater}
              />
            </div>
          </div>
        }
        onClose={handleClose}
      >
        <div className={classes.root}>
          {attachedWorkflows.length > 1 && (
            <AlineAlert severity="warning">{sharedTemplateWarning}</AlineAlert>
          )}
          <div className={classes.twoPane}>
            <div className={classes.configPane}>
              <div className={classes.sectionLabel}>Form Config</div>

              <div className={classes.canvas}>
                <div className={classes.canvasBody}>
                  {fields.length > 0 && (
                    <DndContext
                      collisionDetection={closestCenter}
                      sensors={sensors}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={visibleFields.map(f => f.uid)}
                        strategy={verticalListSortingStrategy}
                      >
                        {fields.map(field =>
                          editor && editor.uid === field.uid ? (
                            <IntakeFormFieldEditor
                              key={field.uid}
                              draft={editor.draft}
                              onCancel={() => setEditor(null)}
                              onChange={draft => setEditor({ ...editor, draft })}
                              onDelete={deleteEditingField}
                              onSave={commitEditor}
                            />
                          ) : (
                            <SortableFieldRow
                              key={field.uid}
                              field={field}
                              onDelete={() => setPendingDeleteField(field)}
                              onEdit={() => setEditor({ draft: field, uid: field.uid })}
                            />
                          )
                        )}
                      </SortableContext>
                    </DndContext>
                  )}

                  {editor && editor.uid === null && (
                    <>
                      {fields.length > 0 && <div className={classes.divider} />}
                      <IntakeFormFieldEditor
                        draft={editor.draft}
                        onCancel={() => setEditor(null)}
                        onChange={draft => setEditor({ ...editor, draft })}
                        onDelete={() => setEditor(null)}
                        onSave={commitEditor}
                      />
                    </>
                  )}

                  {showEmptyGrid && (
                    <div className={classes.grid}>
                      {BUILDER_FIELD_TYPES.map(type => {
                        const { icon: Icon, pickerLabel } = BUILDER_FIELD_META[type];
                        return (
                          <ButtonBase
                            key={type}
                            className={classes.gridCard}
                            type="button"
                            onClick={() => startAdd(type)}
                          >
                            <Icon size={22} strokeWidth={1.75} />
                            <Typography className={classes.gridLabel}>{pickerLabel}</Typography>
                          </ButtonBase>
                        );
                      })}
                    </div>
                  )}
                </div>

                {fields.length > 0 && (
                  <>
                    <ButtonBase
                      ref={setAddAnchor}
                      className={classes.addFieldButton}
                      type="button"
                      onClick={() => setAddOpen(true)}
                    >
                      <CirclePlus size={16} /> Add field
                    </ButtonBase>
                    <Menu
                      anchorEl={addAnchor}
                      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                      open={addOpen}
                      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      onClose={() => setAddOpen(false)}
                    >
                      {BUILDER_FIELD_TYPES.map(type => {
                        const { icon: Icon, pickerLabel } = BUILDER_FIELD_META[type];

                        return (
                          <MenuItem
                            key={type}
                            className={classes.menuItem}
                            onClick={() => startAdd(type)}
                          >
                            <Icon size={18} strokeWidth={1.75} />
                            <Typography>{pickerLabel}</Typography>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}
              </div>
            </div>

            <div className={classes.previewPane}>
              <div className={classes.sectionLabel}>Form Preview</div>
              <div className={classes.previewBox}>
                {previewFields.length > 0 ? (
                  <div className={classes.previewBody}>
                    <IntakeFormFields
                      disableFileUpload
                      fields={previewFields}
                      formDefinition={previewDefinition}
                      showValidation={false}
                      values={previewValues}
                      onChange={(name, value) =>
                        setPreviewValues(prev => ({ ...prev, [name]: value }))
                      }
                      onRemoveFile={handleDisabledPreviewFileRemoval}
                    />
                  </div>
                ) : (
                  <div className={classes.previewEmpty}>Add fields to see a live preview.</div>
                )}
              </div>
            </div>
          </div>

          <div className={classes.footer}>
            <AlineButton color="primary" variant="outlined" onClick={handleClose}>
              Cancel
            </AlineButton>
            <AlineButton color="primary" variant="contained" onClick={handleClose}>
              Done
            </AlineButton>
          </div>
        </div>
      </AlineDialog>
      <ConfirmationDialog
        message={`Are you sure you want to delete "${
          pendingDeleteField?.label || 'Untitled Field'
        }"? This action cannot be undone.`}
        open={Boolean(pendingDeleteField)}
        title="Delete field?"
        onClickPrimary={confirmDeleteField}
        onClose={() => setPendingDeleteField(null)}
      />
    </>
  );
}

interface SortableFieldRowProps {
  field: BuilderField;
  onDelete: () => void;
  onEdit: () => void;
}

function SortableFieldRow({ field, onDelete, onEdit }: SortableFieldRowProps) {
  const { classes } = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: field.uid,
  });
  const { icon: Icon, subtitle } = BUILDER_FIELD_META[field.type];

  return (
    <div
      ref={setNodeRef}
      className={classes.fieldRow}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className={classes.handle} {...listeners} {...attributes}>
        <GripVertical size={16} />
      </div>
      <Icon color="#374951" size={18} />
      <div className={classes.fieldMeta}>
        <div className={classes.fieldLabel}>{field.label || 'Untitled field'}</div>
        <div className={classes.fieldSubtitle}>{subtitle}</div>
      </div>
      <div className={`${classes.rowActions} intake-field-row-actions`}>
        <ButtonBase aria-label="Edit field" className={classes.rowAction} onClick={onEdit}>
          <SquarePen size={18} />
        </ButtonBase>
        <ButtonBase aria-label="Delete field" className={classes.rowAction} onClick={onDelete}>
          <Trash2 size={18} />
        </ButtonBase>
      </div>
    </div>
  );
}
